/**
 * Workflow Node Types Registry
 * Frontend node type definitions matching backend NodeTypes.mjs
 */

import { MaterialIcons } from '@expo/vector-icons';

// Node Type Categories
export const NODE_CATEGORIES = {
  TRIGGER: 'trigger',
  ACTION: 'action',
  LOGIC: 'logic',
  DATA: 'data',
  ERROR: 'error'
};

// Node Type Definitions
export const WORKFLOW_NODE_TYPES = {
  // TRIGGERS
  webhook: {
    type: 'webhook',
    category: NODE_CATEGORIES.TRIGGER,
    name: 'Webhook',
    description: 'Trigger workflow via HTTP webhook',
    icon: 'webhook' as keyof typeof MaterialIcons.glyphMap,
    color: '#ff6d5a',
    inputs: 0,
    outputs: 1
  },
  schedule: {
    type: 'schedule',
    category: NODE_CATEGORIES.TRIGGER,
    name: 'Schedule',
    description: 'Trigger workflow on a schedule',
    icon: 'schedule' as keyof typeof MaterialIcons.glyphMap,
    color: '#00d9ff',
    inputs: 0,
    outputs: 1
  },
  manual: {
    type: 'manual',
    category: NODE_CATEGORIES.TRIGGER,
    name: 'Manual Trigger',
    description: 'Manually trigger workflow',
    icon: 'play-arrow' as keyof typeof MaterialIcons.glyphMap,
    color: '#635bff',
    inputs: 0,
    outputs: 1
  },
  dataChange: {
    type: 'dataChange',
    category: NODE_CATEGORIES.TRIGGER,
    name: 'Data Change',
    description: 'Trigger when data changes',
    icon: 'database' as keyof typeof MaterialIcons.glyphMap,
    color: '#ff6d5a',
    inputs: 0,
    outputs: 1
  },
  httpRequest: {
    type: 'httpRequest',
    category: NODE_CATEGORIES.TRIGGER,
    name: 'HTTP Request',
    description: 'Trigger via HTTP request',
    icon: 'http' as keyof typeof MaterialIcons.glyphMap,
    color: '#ff6d5a',
    inputs: 0,
    outputs: 1
  },

  // ACTIONS
  email: {
    type: 'email',
    category: NODE_CATEGORIES.ACTION,
    name: 'Send Email',
    description: 'Send an email',
    icon: 'mail' as keyof typeof MaterialIcons.glyphMap,
    color: '#6bcf7f',
    inputs: 1,
    outputs: 1
  },
  calendar: {
    type: 'calendar',
    category: NODE_CATEGORIES.ACTION,
    name: 'Calendar Event',
    description: 'Create calendar event',
    icon: 'calendar-today' as keyof typeof MaterialIcons.glyphMap,
    color: '#6bcf7f',
    inputs: 1,
    outputs: 1
  },
  project: {
    type: 'project',
    category: NODE_CATEGORIES.ACTION,
    name: 'Create Project',
    description: 'Create a new project',
    icon: 'folder' as keyof typeof MaterialIcons.glyphMap,
    color: '#6bcf7f',
    inputs: 1,
    outputs: 1
  },
  document: {
    type: 'document',
    category: NODE_CATEGORIES.ACTION,
    name: 'Generate Document',
    description: 'Generate a document',
    icon: 'description' as keyof typeof MaterialIcons.glyphMap,
    color: '#6bcf7f',
    inputs: 1,
    outputs: 1
  },
  integration: {
    type: 'integration',
    category: NODE_CATEGORIES.ACTION,
    name: 'Integration',
    description: 'Execute integration action',
    icon: 'link' as keyof typeof MaterialIcons.glyphMap,
    color: '#6bcf7f',
    inputs: 1,
    outputs: 1
  },
  database: {
    type: 'database',
    category: NODE_CATEGORIES.ACTION,
    name: 'Database',
    description: 'Execute database operation',
    icon: 'storage' as keyof typeof MaterialIcons.glyphMap,
    color: '#6bcf7f',
    inputs: 1,
    outputs: 1
  },

  // LOGIC
  if: {
    type: 'if',
    category: NODE_CATEGORIES.LOGIC,
    name: 'IF',
    description: 'Conditional routing (IF/ELSE)',
    icon: 'code' as keyof typeof MaterialIcons.glyphMap,
    color: '#ffd93d',
    inputs: 1,
    outputs: 2
  },
  switch: {
    type: 'switch',
    category: NODE_CATEGORIES.LOGIC,
    name: 'Switch',
    description: 'Multi-way conditional routing',
    icon: 'swap-horiz' as keyof typeof MaterialIcons.glyphMap,
    color: '#ffd93d',
    inputs: 1,
    outputs: -1 // Dynamic
  },
  loop: {
    type: 'loop',
    category: NODE_CATEGORIES.LOGIC,
    name: 'Loop',
    description: 'Loop over items',
    icon: 'loop' as keyof typeof MaterialIcons.glyphMap,
    color: '#ffd93d',
    inputs: 1,
    outputs: 1
  },
  merge: {
    type: 'merge',
    category: NODE_CATEGORIES.LOGIC,
    name: 'Merge',
    description: 'Merge data from multiple branches',
    icon: 'merge-type' as keyof typeof MaterialIcons.glyphMap,
    color: '#ffd93d',
    inputs: -1, // Dynamic
    outputs: 1
  },
  split: {
    type: 'split',
    category: NODE_CATEGORIES.LOGIC,
    name: 'Split',
    description: 'Split data into multiple items',
    icon: 'call-split' as keyof typeof MaterialIcons.glyphMap,
    color: '#ffd93d',
    inputs: 1,
    outputs: 1
  },

  // DATA
  set: {
    type: 'set',
    category: NODE_CATEGORIES.DATA,
    name: 'Set',
    description: 'Set/update data fields',
    icon: 'edit' as keyof typeof MaterialIcons.glyphMap,
    color: '#ff9500',
    inputs: 1,
    outputs: 1
  },
  transform: {
    type: 'transform',
    category: NODE_CATEGORIES.DATA,
    name: 'Transform',
    description: 'Transform data structure',
    icon: 'transform' as keyof typeof MaterialIcons.glyphMap,
    color: '#ff9500',
    inputs: 1,
    outputs: 1
  },
  filter: {
    type: 'filter',
    category: NODE_CATEGORIES.DATA,
    name: 'Filter',
    description: 'Filter data based on conditions',
    icon: 'filter-list' as keyof typeof MaterialIcons.glyphMap,
    color: '#ff9500',
    inputs: 1,
    outputs: 1
  },
  aggregate: {
    type: 'aggregate',
    category: NODE_CATEGORIES.DATA,
    name: 'Aggregate',
    description: 'Aggregate data (sum, avg, count)',
    icon: 'functions' as keyof typeof MaterialIcons.glyphMap,
    color: '#ff9500',
    inputs: 1,
    outputs: 1
  },
  code: {
    type: 'code',
    category: NODE_CATEGORIES.DATA,
    name: 'Code',
    description: 'Execute custom JavaScript code',
    icon: 'code' as keyof typeof MaterialIcons.glyphMap,
    color: '#ff9500',
    inputs: 1,
    outputs: 1
  },

  // ERROR
  errorTrigger: {
    type: 'errorTrigger',
    category: NODE_CATEGORIES.ERROR,
    name: 'Error Trigger',
    description: 'Catch errors from previous nodes',
    icon: 'error-outline' as keyof typeof MaterialIcons.glyphMap,
    color: '#ff4444',
    inputs: 1,
    outputs: 1
  },
  errorHandler: {
    type: 'errorHandler',
    category: NODE_CATEGORIES.ERROR,
    name: 'Error Handler',
    description: 'Handle errors with custom logic',
    icon: 'error' as keyof typeof MaterialIcons.glyphMap,
    color: '#ff4444',
    inputs: 1,
    outputs: 1
  }
};

// Get node type by type string
export function getNodeType(type: string) {
  return WORKFLOW_NODE_TYPES[type as keyof typeof WORKFLOW_NODE_TYPES];
}

// Get all node types by category
export function getNodeTypesByCategory(category: string) {
  return Object.values(WORKFLOW_NODE_TYPES).filter(nt => nt.category === category);
}

// Get all trigger node types
export function getTriggerNodeTypes() {
  return getNodeTypesByCategory(NODE_CATEGORIES.TRIGGER);
}

// Get all action node types
export function getActionNodeTypes() {
  return getNodeTypesByCategory(NODE_CATEGORIES.ACTION);
}

// Get all logic node types
export function getLogicNodeTypes() {
  return getNodeTypesByCategory(NODE_CATEGORIES.LOGIC);
}

// Get all data node types
export function getDataNodeTypes() {
  return getNodeTypesByCategory(NODE_CATEGORIES.DATA);
}

// Get all error node types
export function getErrorNodeTypes() {
  return getNodeTypesByCategory(NODE_CATEGORIES.ERROR);
}

// Get node color by category
export function getNodeColorByCategory(category: string): string {
  const categoryColors: Record<string, string> = {
    [NODE_CATEGORIES.TRIGGER]: '#ff6d5a',
    [NODE_CATEGORIES.ACTION]: '#6bcf7f',
    [NODE_CATEGORIES.LOGIC]: '#ffd93d',
    [NODE_CATEGORIES.DATA]: '#ff9500',
    [NODE_CATEGORIES.ERROR]: '#ff4444'
  };
  return categoryColors[category] || '#94a3b8';
}

// Get node icon by type
export function getNodeIcon(type: string): keyof typeof MaterialIcons.glyphMap {
  const nodeType = getNodeType(type);
  return nodeType?.icon || 'settings';
}

// Check if type is a workflow node type
export function isWorkflowNodeType(type: string): boolean {
  return type in WORKFLOW_NODE_TYPES;
}

export default {
  NODE_CATEGORIES,
  WORKFLOW_NODE_TYPES,
  getNodeType,
  getNodeTypesByCategory,
  getTriggerNodeTypes,
  getActionNodeTypes,
  getLogicNodeTypes,
  getDataNodeTypes,
  getErrorNodeTypes,
  getNodeColorByCategory,
  getNodeIcon,
  isWorkflowNodeType
};















